import { PureComponent } from 'react';

// Material UI imports
import {
  Typography,
  ButtonGroup,
  Button,
  Avatar,
  withStyles,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress
} from '@material-ui/core';

import { AssignmentRounded as LessonIcon } from '@mui/icons-material';

// Import lab components
import { Rating, Skeleton } from '@material-ui/lab';

// React routing link
import { withRouter } from 'react-router-dom';

// Portlet items
import {
  GroupSessionBookingInfo,
  Portlet,
  PortletContent,
  PortletFooter,
  PortletHeader
} from 'components';

// Name initials helper
import { getErrorMessage, getInitials } from 'helpers';

// DateTime helpers
import { DateTime } from 'luxon';

// Style
import styles from './styles';
import {
  getMaterialLevelInfo,
  getMaterialLevelInfoPTE
} from 'services/materials';

class GroupSessionTutorBookingCard extends PureComponent {
  constructor(props) {
    super(props);

    // Initialize the component's state
    const { loading, bookings } = props;
    this.state = {
      selected:
        !loading && bookings.length > 0 ? bookings[0].groupSessionId : null,
      material: null,
      course: null,
      lesson: null,
      popoverAnchor: null,
      isLessonSelectionOpen: false,
      selectedLessons: [],
      isBooking: false,
      bookingError: null,
      lessons: []
    };

    this.ready = false;
  }

  async componentDidMount() {
    this.ready = true; // Component is now mounted
    await this.fetchMaterials();
  }

  componentDidUpdate(prevProps) {
    const { loading, bookings } = this.props;
    if (prevProps.bookings !== bookings) {
      this.setState({
        selected:
          !loading && bookings.length > 0 ? bookings[0].groupSessionId : null
      });
      this.fetchMaterials();
    }
  }

  componentWillUnmount() {
    this.ready = false;
  }

  // Function to fetch material, course, and lesson data
  async fetchMaterials() {
    const { bookings, auth } = this.props;
    const { info } = bookings[0] || {};

    if (!info?.topic) return;

    try {
      const [materials, courses] = await Promise.all([
        auth.api.materials.getMaterials(),
        auth.api.materials.getCourses(info.topic.material)
      ]);

      const material = materials.find(
        (entry) => entry.id === info.topic.material
      );
      const course = courses.find((entry) => entry.id === info.topic.course);

      if (course && material) {
        const lesson = course.lessons.find(
          (lesson) => lesson.id === info.topic.lesson
        );

        if (this.ready) {
          this.setState({
            material,
            course,
            lesson,
            lessons: course.lessons
          });
        }
      } else {
        throw new Error('Material or course not found');
      }
    } catch (error) {
      if (this.ready) {
        this.setState({
          error: getErrorMessage(error)
        });
      }
    }
  }

  // Event handler to open popover
  handlePopoverOpen = (event) => {
    this.setState({
      popoverAnchor: event.currentTarget
    });
  };

  // Event handler to close popover
  handlePopoverClose = () => {
    this.setState({
      popoverAnchor: null
    });
  };

  // Open lesson selection dialog
  handleOpenLessonSelection = () => {
    const { bookings } = this.props;

    // Get lesson IDs for BOOKED lessons, past lessons, and available future lessons
    // Include all lessons that are either booked or have time in the past
    const lessonIds = bookings
      .filter(
        (booking) =>
          booking.status === 'booked' ||
          DateTime.fromMillis(booking.when) < DateTime.now()
      )
      .map((booking) => booking.id);

    this.setState({
      isLessonSelectionOpen: true,
      // Initialize with all booked and past lessons selected
      selectedLessons: lessonIds
    });
  };

  // Close lesson selection dialog
  handleCloseLessonSelection = () => {
    this.setState({
      isLessonSelectionOpen: false
    });
  };

  // Handle lesson selection checkbox changes
  handleLessonSelectionChange = (lessonNumber) => {
    const { bookings } = this.props;

    this.setState((prevState) => {
      const { selectedLessons } = prevState;

      if (selectedLessons.includes(lessonNumber)) {
        // Remove lesson if already selected
        return {
          selectedLessons: selectedLessons.filter((id) => id !== lessonNumber),
          bookingError: null
        };
      } else {
        // Check if the lesson being added conflicts with already selected lessons
        const lessonToAdd = bookings.find((b) => b.id === lessonNumber);
        const lessonToAddTime = lessonToAdd?.when;

        // Check for time conflicts with already selected lessons
        const hasTimeConflict = selectedLessons.some((selectedId) => {
          const selectedBooking = bookings.find((b) => b.id === selectedId);
          return selectedBooking?.when === lessonToAddTime;
        });

        if (hasTimeConflict) {
          return {
            bookingError:
              'You cannot select multiple lessons that occur at the same time. Please select lessons at different times.'
          };
        }

        // Add lesson if not selected and no conflicts
        return {
          selectedLessons: [...selectedLessons, lessonNumber].sort(
            (a, b) => a - b
          ),
          bookingError: null
        };
      }
    });
  };

  // Handle booking the selected lessons

  handleBookSelectedLessons = async () => {
    const { onBook, bookings } = this.props;
    const { selectedLessons, course, lesson } = this.state;
    const groupSessionId = bookings[0].groupSessionId;
    const bookingTime = bookings[0].when;

    if (selectedLessons.length === 0) {
      this.setState({
        bookingError: 'Please select at least one lesson to book'
      });
      return;
    }

    this.setState({ isBooking: true, bookingError: null });

    try {
      // Prepare the booking details
      const groupSessionDetail = {
        lessonCount: selectedLessons.length,
        // Ensure selectedLessons is always sent as an array
        selectedLessons: Array.isArray(selectedLessons)
          ? selectedLessons
          : [selectedLessons],
        courseName: course?.name,
        lessonLevel: lesson?.level,
        groupSessionId: groupSessionId,
        when: bookingTime
      };

      console.log('Booking payload:', groupSessionDetail); // Debug log

      // Call the onBook function passed from parent
      await onBook(groupSessionDetail);

      // Close the dialog after successful booking
      this.setState({
        isLessonSelectionOpen: false,
        isBooking: false
      });
    } catch (error) {
      this.setState({
        bookingError: getErrorMessage(error),
        isBooking: false
      });
    }
  };

  render() {
    const { classes, loading, bookings, tutor, onBook } = this.props;
    const {
      selected,
      course,
      lesson,
      popoverAnchor,
      isLessonSelectionOpen,
      selectedLessons,
      isBooking,
      bookingError,
      lessons
    } = this.state;
    console.log({ lesson });
    if (!bookings || bookings.length === 0 || loading) {
      return (
        <Portlet className={classes.root}>
          <PortletHeader>
            <Typography variant="h5">
              <Skeleton width={200} />
            </Typography>
            <Typography variant="h5">
              <Skeleton width={100} />
            </Typography>
          </PortletHeader>
          <PortletContent className={classes.content}>
            <Skeleton variant="circle" className={classes.avatar} />
            <div className={classes.info}>
              <Skeleton variant="rect" width={120} height={20} />
              <Skeleton variant="text" width={150} />
              <Skeleton variant="text" width={200} />
              <Skeleton variant="text" width={180} />
            </div>
          </PortletContent>
        </Portlet>
      );
    }

    const { image, name, isPte, rating } = bookings[0]?.tutor;
    const { topic } = bookings[0].info;
    // Use actual bookings length instead of lessonCount field which may be incorrect
    const lessonCount = bookings.length;
    const bookingTime = bookings[0].when;

    // Format the time
    const time = DateTime.fromMillis(bookingTime);

    // Format the time to the desired format: "Tuesday 12-Nov-2024"
    const commencingTime = time.toFormat('cccc dd-MMM-yyyy');

    // Format the day and time, appending "'s" for possessive form
    const dayFormatted = time.toFormat('cccc');
    const timeFormatted = time.toFormat('h:mm a');
    const formattedTime = `${commencingTime} at ${timeFormatted}`;

    // Render the tutor booking card
    return (
      <>
        {/* Info Popover */}
        <Popover
          id={popoverAnchor ? 'lesson-info-popover' : undefined}
          open={Boolean(popoverAnchor)}
          anchorEl={popoverAnchor}
          onClose={this.handlePopoverClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          PaperProps={{
            style: {
              maxHeight: '300px',
              overflow: 'auto'
            }
          }}>
          <GroupSessionBookingInfo bookings={bookings} />
        </Popover>

        {/* Lesson Selection Dialog */}
        <Dialog
          open={isLessonSelectionOpen}
          onClose={this.handleCloseLessonSelection}
          aria-labelledby="lesson-selection-dialog-title"
          maxWidth="md"
          fullWidth>
          <DialogTitle id="lesson-selection-dialog-title">
            Select Lessons to Book
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="subtitle1" gutterBottom>
              {course?.name} Level {lesson?.level} - {lessonCount} Lessons
              Available
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Please select the lessons you would like to book with {name}.
            </Typography>

            <FormGroup>
              {bookings
                ?.sort((a, b) => {
                  const lessonA = lessons.find(
                    (lesson) => lesson.id === a.info.topic.lesson
                  );
                  const lessonB = lessons.find(
                    (lesson) => lesson.id === b.info.topic.lesson
                  );
                  return (lessonA?.code || '').localeCompare(
                    lessonB?.code || ''
                  );
                })
                ?.map((booking) => {
                  const curentLessons = lessons.find(
                    (lesson) => lesson.id === booking?.info.topic.lesson
                  );
                  const isA2 = curentLessons?.code.includes('AA2');
                  const levelInfo =
                    booking?.info.topic.material == 'pte'
                      ? getMaterialLevelInfoPTE(
                          isA2,
                          curentLessons?.level,
                          true
                        )
                      : getMaterialLevelInfo(curentLessons?.code);

                  return (
                    <FormControlLabel
                      key={booking?.id}
                      control={
                        <Checkbox
                          checked={selectedLessons.includes(booking?.id)}
                          disabled={
                            DateTime.fromMillis(booking?.when) <
                              DateTime.now() ||
                            (booking?.status !== 'pending' &&
                              booking?.status !== 'open')
                          }
                          onChange={() =>
                            this.handleLessonSelectionChange(booking?.id)
                          }
                          name={`lesson-${booking?.id}`}
                          color="primary"
                        />
                      }
                      label={
                        <>
                          <b>{curentLessons?.code}</b>
                          {` - ${curentLessons?.name} - `}
                          <b>
                            {DateTime.fromMillis(booking?.when).toFormat(
                              'cccc dd-MMM-yyyy h:mm a'
                            )}
                            {
                              // show status of the boooking if booked
                              booking?.status === 'booked'
                                ? ` ( ${booking?.status.toUpperCase()} )`
                                : ''
                            }
                          </b>
                        </>
                      }
                    />
                  );
                })}
            </FormGroup>

            {bookingError && (
              <Typography
                variant="body2"
                color="error"
                style={{ marginTop: 16 }}>
                {bookingError}
              </Typography>
            )}

            <Typography variant="body2" style={{ marginTop: 16 }}>
              Selected: {selectedLessons.length} of {lessonCount} lessons
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={this.handleCloseLessonSelection}
              color="default"
              disabled={isBooking}>
              Cancel
            </Button>
            <Button
              onClick={this.handleBookSelectedLessons}
              color="primary"
              variant="contained"
              disabled={selectedLessons.length === 0 || isBooking}
              startIcon={isBooking ? <CircularProgress size={20} /> : null}>
              {isBooking ? 'Booking...' : 'Book Selected Lessons'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Main Card Component */}
        <Portlet className={classes.root}>
          <PortletHeader>
            <div style={{ textAlign: 'center', width: '100%' }}>
              <Typography variant="h6" style={{ fontWeight: 600 }}>
                {course && lesson
                  ? `${course.name}`
                  : topic?.course || 'Course not available'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Level {lesson?.level} | {lessonCount} Lessons
              </Typography>
            </div>
          </PortletHeader>
          <PortletContent className={classes.content}>
            <Avatar src={image} alt={name} className={classes.avatar}>
              <Typography variant="h3" style={{ color: 'white' }}>
                {getInitials(name || '')}
              </Typography>
            </Avatar>
            <div className={classes.info}>
              <Rating
                readOnly
                size="small"
                precision={0.1}
                value={!rating ? 0 : rating?.avg}
                className={classes.rating}
              />
              <Typography
                variant="h6"
                style={{ textAlign: 'center', marginTop: '8px' }}>
                {isPte ? 'PTE Workplace' : 'Workplace'}
              </Typography>
              <Typography variant="h3">{name}</Typography>

              {/* Display formatted time */}
              {/* <Typography variant="h6" className={classes.subtitle}>
                {formattedTime}{' '}
              </Typography> */}
            </div>
            <div style={{ margin: '10px 0px' }}>
              {/* <Button
                size="small"
                className={classes.gutterRight}
                variant="contained"
                onClick={(ev) => this.handlePopoverOpen(ev)}
                startIcon={<LessonIcon />}>
                View Lessons
              </Button> */}
              <Button
                className={classes.gutterRight}
                size="small"
                variant="contained"
                onClick={() => {
                  const { history } = this.props;
                  // console.log(tutor);
                  // console.log(bookings[0]?.tutor?.uid);
                  history.push(`/teacher/profile/${bookings[0]?.tutor?.uid}`);
                }}>
                view teacher
              </Button>
            </div>

            <ButtonGroup fullWidth variant="text">
              <Button
                className={classes.commencingTime}
                onClick={this.handleOpenLessonSelection}>
                View & Book Lessons
              </Button>
            </ButtonGroup>
          </PortletContent>
          <PortletFooter className={classes.buttonFill} noPadding>
            <div className={classes.commencingBadge}>
              <Typography>Commencing {commencingTime}</Typography>{' '}
            </div>
          </PortletFooter>
        </Portlet>
      </>
    );
  }
}

export default withRouter(withStyles(styles)(GroupSessionTutorBookingCard));
